<template>
  <div
    class="kiosk-order-type-v2 fade-in"
    :style="pageStyle"
  >
    <div
      v-if="store && !isAutoSelecting"
      class="kiosk-order-type-content"
    >
      <div class="kiosk-main-content">
        <!-- Header: Logo & Title -->
        <div class="kiosk-header-section">
          <img
            :src="logoSource"
            class="kiosk-logo-v2"
            alt="Logo"
          >
          <h3 class="kiosk-order-type-title">
            Select order type
          </h3>
        </div>

        <!-- Main: Dining Options -->
        <div class="kiosk-order-type-section">
          <div class="kiosk-order-type-buttons">
            <button
              v-for="dining_option in activeDiningOptions"
              :key="dining_option.id"
              @click="handleDiningOptionClick(dining_option)"
              class="kiosk-order-type-btn"
            >
              <span>{{ dining_option.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Footer: info & powered -->
      <div class="kiosk-footer-v2">
        <div
          v-if="terminal"
          class="terminal-version-v2"
        >
          {{ terminal.name }} - {{ terminal.id }}
        </div>
        <img
          v-long-press="5000"
          src="@/assets/images/powered.svg"
          class="kiosk-powered-v2"
          @long-press-start="goToTools"
          alt="Powered by"
        >
      </div>
    </div>
    <scale-loader
      v-if="!store || isAutoSelecting"
      :color="spinner.color"
      :size="spinner.size"
    />
  </div>
</template>

<script>
import LongPress from "vue-directive-long-press";

export default {
  name: "KioskOrderTypeV2",
  components: {
    ScaleLoader: () => import("vue-spinner/src/ScaleLoader.vue"),
  },
  directives: {
    "long-press": LongPress,
  },
  data() {
    return {
      spinner: {
        size: "50px",
        color: "#2563EB",
      },
      selectedDiningOptionId: null,
      selectedDiningOption: null,
      isAutoSelecting: false,
    };
  },
  created() {
    this.checkAutoSelect();
  },
  computed: {
    terminal() {
      return this.$store.getters["config/terminal"];
    },
    store() {
      return this.$store.getters["config/store"];
    },
    isWhitelabelEnabled() {
      return this.$store.getters["config/isWhitelabelEnabled"];
    },
    primaryColor() {
      return this.$store.getters["config/primaryColor"];
    },
    whitelabelLogo() {
      return this.$store.getters["config/whitelabelLogo"];
    },
    logoSource() {
      return this.isWhitelabelEnabled && this.whitelabelLogo
        ? this.whitelabelLogo
        : this.store?.logo || require("@/assets/images/logo.svg");
    },
    pageStyle() {
      const styles = {};

      if (this.store?.background) {
        styles.backgroundImage = `linear-gradient(rgba(15, 23, 42, 0.5), rgba(15, 23, 42, 0.8)), url(${this.store.background})`;
        styles.backgroundSize = "cover";
        styles.backgroundPosition = "center";
      } else if (this.isWhitelabelEnabled && this.primaryColor) {
        styles.background = `linear-gradient(180deg, ${this.primaryColor} 0%, ${this.primaryColor} 100%)`;
      }

      return styles;
    },
    activeDiningOptions() {
      if (!this.terminal || !this.terminal.dining_options) return [];
      return this.terminal.dining_options.filter((option) => option.active);
    },
  },
  watch: {
    terminal() {
      this.checkAutoSelect();
    },
  },
  methods: {
    goToTools() {
      this.$router.push({ name: "Tools" });
    },
    checkAutoSelect() {
      if (
        this.terminal &&
        this.activeDiningOptions &&
        this.activeDiningOptions.length === 1
      ) {
        this.isAutoSelecting = true;
        this.handleDiningOptionClick(this.activeDiningOptions[0]);
      }
    },
    handleDiningOptionClick(diningOption) {
      this.selectedDiningOptionId = diningOption.id;
      this.selectedDiningOption = diningOption;
      this.createOrder(diningOption.id);
    },
    async createOrder(diningOptionId) {
      const newOrder = await this.$store.dispatch("orders/initNewOrder", {
        related_terminal: this.terminal.id,
      });

      // add dining options in kiosk mode.
      if (!newOrder.dining_option_type) {
        newOrder.dining_option_type = diningOptionId;
        newOrder.dining_option = this.activeDiningOptions.find(
          (option) => newOrder.dining_option_type === option.id,
        )?.name;
      }

      this.$store.dispatch("orders/saveOrder", newOrder);
      await this.$router.push({
        name: "OrderV2",
        params: { id: newOrder.local_id, diningOption: diningOptionId },
      });
    },
  },
};
</script>

<style scoped>
.kiosk-order-type-v2 {
  height: 100vh;
  width: 100%;
  background: linear-gradient(rgba(15, 23, 42, 0.5), rgba(15, 23, 42, 0.8));
  color: #fff;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  overflow: hidden;
}

.kiosk-order-type-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  padding: 20px 0;
}

.kiosk-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
}

.kiosk-header-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.kiosk-logo-v2 {
  max-height: 120px;
  max-width: 300px;
  object-fit: contain;
}

.kiosk-order-type-title {
  font-size: 36px;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  font-family: "Outfit", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kiosk-order-type-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  padding-top: 10px;
}

.kiosk-order-type-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 32px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.kiosk-order-type-btn {
  background: white;
  color: #0f172a;
  font-weight: 800;
  font-size: 24px;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: none;
  border-bottom: 4px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  min-width: 280px;
  max-width: 320px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
}

.kiosk-order-type-btn:hover {
  background: #f9fafb;
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.kiosk-order-type-btn:active {
  transform: scale(0.96);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.kiosk-footer-v2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  width: 100%;
  padding-bottom: 20px;
}

.terminal-version-v2 {
  font-size: 16px;
  opacity: 0.8;
  font-weight: 500;
}

.kiosk-powered-v2 {
  max-height: 80px;
  cursor: pointer;
}

@media (max-height: 768px) {
  .kiosk-logo-v2 {
    max-height: 80px;
  }

  .kiosk-order-type-title {
    font-size: 28px;
  }

  .kiosk-order-type-btn {
    padding: 32px 24px;
    font-size: 22px;
    min-width: 260px;
  }
}
</style>
